// @ts-nocheck
import { Enveloper } from "./enveloper.js";
import { EnvelopeControlRegister } from "./registers/envelope-control-registers.js";
import { SweepControlRegister } from "./registers/sweep-control-register.js";
import { SimpleByteRegister } from "../helpers/simple-byte-register.js";
import { HighOrderFrequencyRegister } from "./registers/high-order-frequency-registers.js";
// Note: No sounds write out to the nr52 register currently. If a game checks audio enabled state, it currently won't be set
export class Sound1 {
    dutyCycles = [
        [0, 0, 0, 0, 0, 0, 0, 1], // 12.5 %
        [1, 0, 0, 0, 0, 0, 0, 1], // 25 %
        [1, 0, 0, 0, 0, 1, 1, 1], // 50 %
        [0, 1, 1, 1, 1, 1, 1, 0], // 75 %
    ];
    positionInDutyCycle = 0;
    frequencyTimer = 0;
    frequencyPeriod = 0;
    lengthTimer = 0;
    enveloper = new Enveloper();
    volume = 0;
    isSweepEnabled = false;
    shadowFrequency = 0;
    sweepTimer = 0;
    nr10SweepControl = new SweepControlRegister(0xff10);
    nr11LengthAndDutyCycle = new SimpleByteRegister(0xff11);
    nr12EnvelopeControl = new EnvelopeControlRegister(0xff12);
    nr13LowOrderFrequency = new SimpleByteRegister(0xff13);
    nr14HighOrderFrequency = new HighOrderFrequencyRegister(0xff14);
    isActive = false;
    // Live channel-on status for NR52 ($FF26) bit 0. A channel is "on" while
    // its length counter (or sweep, or the DAC) hasn't silenced it — exactly
    // the bit a game polls to wait for a note to finish.
    get isOn() {
        return this.isActive;
    }
    writeNr14(value) {
        this.nr14HighOrderFrequency.value = value;
        if ((value & 0x80) !== 0) {
            this.playSound();
        }
    }
    // NR12 ($FF12) write. Writing a DAC-disabling value (initial volume 0 AND
    // envelope direction down — i.e. the top 5 bits all clear) turns the DAC
    // off, which on hardware immediately disables the channel. getSample()
    // already returns 0 when the DAC is off, but NR52's status bit must reflect
    // it too, so clear isActive here.
    writeNr12(value) {
        this.nr12EnvelopeControl.value = value;
        if (!this.nr12EnvelopeControl.isDacEnabled) {
            this.isActive = false;
        }
    }
    // Power-off ($FF26 bit 7 -> 0) clears every channel register and silences
    // the channel; see APU.writeNr52.
    powerOff() {
        this.isActive = false;
        this.nr10SweepControl.value = 0;
        this.nr11LengthAndDutyCycle.value = 0;
        this.nr12EnvelopeControl.value = 0;
        this.nr13LowOrderFrequency.value = 0;
        this.nr14HighOrderFrequency.value = 0;
    }
    tick(cycles) {
        this.frequencyTimer -= cycles; // count down the frequency timer
        if (this.frequencyTimer <= 0) {
            this.frequencyTimer += this.frequencyPeriod; // reload timer with the current frequency period
            this.positionInDutyCycle = (this.positionInDutyCycle + 1) % 8; // advance to next value in current duty cycle, reset to 0 at 8 to loop back
        }
    }
    playSound() {
        // Enable channel
        this.isActive = true;
        this.resetSweepTimer();
        this.shadowFrequency = this.nr13LowOrderFrequency.value | (this.nr14HighOrderFrequency.highOrderFrequencyData << 8);
        this.isSweepEnabled = this.nr10SweepControl.sweepTime !== 0 && this.nr10SweepControl.sweepAmount !== 0;
        // Initialize frequency
        this.frequencyPeriod = this.getFrequencyPeriod();
        this.frequencyTimer = this.frequencyPeriod;
        // Initialize envelope
        this.volume = this.nr12EnvelopeControl.initialVolume;
        this.enveloper.initializeTimer(this.nr12EnvelopeControl.lengthOfEnvelopeStep);
        // Initialize length
        this.lengthTimer = 64 - (this.nr11LengthAndDutyCycle.value & 0b111111);
        // A trigger with the DAC off does not enable the channel (hardware forces
        // it back off immediately).
        if (!this.nr12EnvelopeControl.isDacEnabled) {
            this.isActive = false;
        }
    }
    clockLength() {
        if (!this.nr14HighOrderFrequency.isContinuousSelection) {
            this.lengthTimer--;
            if (this.lengthTimer === 0) {
                this.isActive = false;
            }
        }
    }
    clockVolume() {
        this.volume = this.enveloper.clockVolume(this.volume, this.nr12EnvelopeControl);
    }
    clockSweep() {
        if (this.sweepTimer > 0) {
            this.sweepTimer--;
        }
        if (this.sweepTimer === 0) {
            this.resetSweepTimer();
            if (this.isSweepEnabled) {
                const newFrequency = this.calculateNewSweepFrequency();
                if (newFrequency < 2048 && this.nr10SweepControl.sweepAmount > 0) {
                    this.shadowFrequency = newFrequency;
                    this.frequencyPeriod = ((2048 - newFrequency) * 4);
                }
            }
        }
    }
    resetSweepTimer() {
        this.sweepTimer = this.nr10SweepControl.sweepTime;
        if (this.sweepTimer === 0) {
            this.sweepTimer = 8;
        }
    }
    calculateNewSweepFrequency() {
        const { sweepAmount, isSweepIncrease } = this.nr10SweepControl;
        const shiftedFrequency = this.shadowFrequency >> sweepAmount;
        const shiftFrequencyBy = isSweepIncrease ? -shiftedFrequency : shiftedFrequency;
        const newFrequency = this.shadowFrequency + shiftFrequencyBy;
        if (newFrequency >= 2048) {
            this.isActive = false;
        }
        return newFrequency;
    }
    getSample() {
        if (!this.nr12EnvelopeControl.isDacEnabled || !this.isActive) {
            return 0;
        }
        const sample = this.dutyCycles[this.nr11LengthAndDutyCycle.value >> 6][this.positionInDutyCycle];
        const volumeAdjustedSample = sample * this.volume;
        return volumeAdjustedSample / 15;
    }
    getFrequencyPeriod() {
        const rawValue = this.nr13LowOrderFrequency.value | (this.nr14HighOrderFrequency.highOrderFrequencyData << 8);
        return ((2048 - rawValue) * 4);
    }
}
//# sourceMappingURL=sound-1.js.map